import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { resolveImagePath } from '@/utils/imageResolver';

interface GalleryImage {
  src: string;
  alt: string;
  caption?: string;
}

interface GalleryProps {
  images: GalleryImage[];
  columns?: number;
  className?: string;
}

export function Gallery({ images, columns = 3, className = "" }: GalleryProps) {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const openLightbox = (index: number) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const goToPrevious = () => {
    if (selectedImage !== null && selectedImage > 0) {
      setSelectedImage(selectedImage - 1);
    }
  };

  const goToNext = () => {
    if (selectedImage !== null && selectedImage < images.length - 1) {
      setSelectedImage(selectedImage + 1);
    }
  };

  // Global keyboard navigation when lightbox is open
  useEffect(() => {
    if (selectedImage === null) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      e.preventDefault(); // Prevent default browser behavior
      
      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
      }
    };

    // Add event listener to document for global keyboard navigation
    if (typeof window !== 'undefined') {
      document.addEventListener('keydown', handleKeyDown);
      
      // Cleanup
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [selectedImage]); // Re-run when selectedImage changes

  const gridCols = {
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <>
      {/* Gallery Grid */}
      <div className={`grid ${gridCols[columns as keyof typeof gridCols]} gap-4 ${className}`}>
        {images.map((image, index) => (
          <div
            key={index}
            className="relative group cursor-pointer overflow-hidden rounded-lg bg-gray-200 aspect-square"
            onClick={() => openLightbox(index)}
          >
            <img
              src={resolveImagePath(image.src)}
              alt={image.alt}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
              <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-sm font-medium">
                View Image
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedImage !== null && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            aria-label="Close gallery"
          >
            <X size={32} />
          </button>

          {/* Previous Button */}
          {selectedImage > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToPrevious();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-all duration-200 z-10 bg-black bg-opacity-20 hover:bg-opacity-40 rounded-full p-2"
              aria-label="Previous image (← arrow key)"
              title="Previous image (← arrow key)"
            >
              <ChevronLeft size={40} />
            </button>
          )}

          {/* Next Button */}
          {selectedImage < images.length - 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-all duration-200 z-10 bg-black bg-opacity-20 hover:bg-opacity-40 rounded-full p-2"
              aria-label="Next image (→ arrow key)"
              title="Next image (→ arrow key)"
            >
              <ChevronRight size={40} />
            </button>
          )}

          {/* Image Container */}
          <div
            className="relative max-w-full max-h-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={resolveImagePath(images[selectedImage].src)}
              alt={images[selectedImage].alt}
              className="max-w-full max-h-[80vh] object-contain"
            />
            {images[selectedImage].caption && (
              <p className="text-white text-center mt-4 px-4 text-sm">
                {images[selectedImage].caption}
              </p>
            )}
          </div>

          {/* Image Counter and Navigation Hint */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm text-center">
            <div className="mb-1">
              {selectedImage + 1} of {images.length}
            </div>
            <div className="text-xs text-gray-300">
              Use ← → arrow keys to navigate • ESC to close
            </div>
          </div>
        </div>
      )}
    </>
  );
}